import type { CheckpointSettings, FieldType, FieldTypeOptions } from './field-types';

/**
 * WHAT A CHECKPOINT'S DEADLINE IS, COMPUTED ONCE (`docs/decisions/phase-b-coach.md` D1).
 *
 * ONE IMPLEMENTATION, TWO CALLERS, exactly as `dashboard.ts` next door: `shared` ships
 * source with no build step, so the BROWSER (the checklist section, the dense table cell,
 * the phone card) and the ENGINE (the health rule that counts overdue checkpoints) import
 * the same functions. "Three days late" has to mean the same thing on the screen and in
 * the rule that colours the row red, and that is only true if there is one place it is
 * decided.
 *
 * CALENDAR DAYS, NEVER MILLISECONDS, for the reason `view.ts` states where it does the same
 * arithmetic: `t + 2 * 86_400_000` is off by an hour across a DST boundary and can land on
 * the previous day, so "due in 2 days" would silently be one or three in March and October.
 * The `Date(y, m, d + n)` constructor normalises over month and year ends, which is what
 * makes the month-boundary cases arithmetic rather than special cases.
 *
 * A DAY IS A STRING HERE, `YYYY-MM-DD`, and comparisons are string comparisons: that format
 * sorts lexicographically exactly as it sorts chronologically, so nothing in this file has
 * to parse a date to know whether it is past.
 *
 * IT READS ONE ROW AND NOTHING ELSE. That is what makes a deadline computable in a browser
 * that is holding a list of rows, and it is also the constraint that shaped D1's validation:
 * the base and the blocked-by are DATE fields on the SAME object, so their values are in the
 * same row as the checkpoint's own.
 */

/** A field as both callers already have it: the browser's metadata and the server's spec. */
export interface CheckpointField {
  readonly id: string;
  readonly label: string;
  readonly type: FieldType;
  readonly settings: FieldTypeOptions;
}

/** Logical values by field id, exactly as `RecordResult.values` carries them. */
export type CheckpointRow = Readonly<Record<string, unknown>>;

/**
 * The five states a row can be in. `blocked` is the one that keeps a checklist honest:
 * "not yet possible", not "not done". A 48-hour check-in on a customer whose onboarding
 * call has no date is nobody's overdue task.
 *
 * `waived` is its mirror image, "no longer needed" (finding F6): the checkpoint's
 * `waivedByFieldId` names a later date that is already set, so the step was overtaken by
 * events. It is neither open nor late and it is never the next step.
 */
export type CheckpointStatus = 'done' | 'due' | 'late' | 'blocked' | 'waived';

export interface CheckpointState {
  readonly status: CheckpointStatus;
  /** The deadline, or null when it cannot be computed (no base value, or blocked). */
  readonly deadline: string | null;
  /**
   * SIGNED, in calendar days from today to the deadline: 2 is "due in 2 days", -3 is "3
   * days late", 0 is today. Null when there is no deadline to count to.
   *
   * Signed rather than an absolute number beside a status, because the two would be two
   * places to get the sign wrong and the copy needs `Math.abs` either way.
   */
  readonly days: number | null;
}

/** The checkpoint declaration of a field, or undefined when it carries none. */
export function checkpointOf(field: CheckpointField): CheckpointSettings | undefined {
  return field.settings.checkpoint;
}

/** Every field of the group that is a checkpoint, in the order they were given. */
export function checkpointFieldsOf(fields: Iterable<CheckpointField>): readonly CheckpointField[] {
  return [...fields].filter((field) => field.settings.checkpoint !== undefined);
}

/**
 * Today in the reader's own calendar, which is the one they mean.
 *
 * The browser has its own `todayKey` for due dates and passes it in; this default is for
 * the engine and for every caller that has no clock of its own to hand.
 */
export function checklistToday(now: Date = new Date()): string {
  return isoDay(now);
}

/**
 * THE DEADLINE: base + offsetDays, or null.
 *
 * Null in three cases, and they are three different truths that all render as "no deadline
 * yet": the checkpoint waits on another one that has no date (blocked), the base date is
 * empty, or the row does not carry the base column at all - which D5 names explicitly,
 * because a table view that did not select the base cannot draw a late ring and must draw a
 * plain one rather than guess.
 *
 * WHICH FIELD IS THE BASE. `baseFieldId`, and when that is null the checkpoint named by
 * `blockedByFieldId` - the 48-hour check-in counts from the onboarding call. D1's
 * validation guarantees at least one of the two is there.
 */
export function checkpointDeadline(
  settings: CheckpointSettings,
  row: CheckpointRow,
): string | null {
  const blockedBy = settings.blockedByFieldId;
  if (blockedBy !== undefined && dayValue(row[blockedBy]) === null) return null;
  const baseFieldId = settings.baseFieldId ?? blockedBy;
  if (baseFieldId === undefined) return null;
  const base = dayValue(row[baseFieldId]);
  if (base === null) return null;
  return addDays(base, settings.offsetDays);
}

/**
 * THE STATE OF ONE CHECKPOINT ON ONE ROW.
 *
 * DONE WINS OVER EVERYTHING, including blocked and late: the date is set, the thing
 * happened, and a row that reads "3 days late" under a filled circle is a row nobody
 * believes. It is also why `days` is null for a done checkpoint - the interesting number
 * afterwards is when it happened, which is the date itself.
 *
 * A field with no checkpoint declaration is `due` with no deadline: it is an ordinary date
 * field, empty or not, and this function refuses to invent a deadline for it.
 */
export function checkpointState(
  field: CheckpointField,
  row: CheckpointRow,
  today: string = checklistToday(),
): CheckpointState {
  if (dayValue(row[field.id]) !== null) return { status: 'done', deadline: null, days: null };

  const settings = field.settings.checkpoint;
  if (settings === undefined) return { status: 'due', deadline: null, days: null };

  /**
   * WAIVED WINS OVER BLOCKED AND LATE, AND LOSES ONLY TO DONE. The later date that waives
   * this step is a fact about the client (the onboarding call happened), so a step it
   * overtook is neither "not yet possible" nor "26 days late".
   */
  const waivedBy = settings.waivedByFieldId;
  if (waivedBy !== undefined && dayValue(row[waivedBy]) !== null) {
    return { status: 'waived', deadline: null, days: null };
  }

  const blockedBy = settings.blockedByFieldId;
  if (blockedBy !== undefined && dayValue(row[blockedBy]) === null) {
    return { status: 'blocked', deadline: null, days: null };
  }

  const deadline = checkpointDeadline(settings, row);
  if (deadline === null) return { status: 'due', deadline: null, days: null };

  const days = daysBetween(today, deadline);
  return { status: days < 0 ? 'late' : 'due', deadline, days };
}

/**
 * IS ANY CHECKPOINT ON THIS ROW PAST ITS DEADLINE (D6's "Late" chip).
 *
 * A BROWSER PREDICATE OVER LOADED ROWS, and it has to be: the deadline is per-row
 * arithmetic over two of the row's own columns, which no stored filter can express -
 * `shared/view.ts`'s operand set has no "field plus offset" comparison and never will.
 */
export function rowIsLate(
  row: CheckpointRow,
  checkpointFields: readonly CheckpointField[],
  today: string = checklistToday(),
): boolean {
  return checkpointFields.some((field) => checkpointState(field, row, today).status === 'late');
}

export interface OpenCheckpoint {
  readonly field: CheckpointField;
  readonly state: CheckpointState;
}

/**
 * THE NEXT THING TO DO ON THIS ROW: the earliest-deadline checkpoint that is not done,
 * not blocked and not waived, for the card's "Next: 48-hour check-in - 3 days late" line (D5).
 *
 * BY DEADLINE, NOT BY DECLARATION ORDER, because the declaration order is the order the
 * fields were written down and the deadlines are what the day is actually shaped by - a
 * checkpoint due tomorrow is the next one even if it was declared last. A checkpoint with
 * no deadline (its base is empty, or absent from the row) sorts LAST among the open ones
 * and still wins over nothing at all: something open is more useful to name than silence.
 * Ties keep declaration order, which is stable and is the order the section renders in.
 */
export function nextOpenCheckpoint(
  row: CheckpointRow,
  checkpointFields: readonly CheckpointField[],
  today: string = checklistToday(),
): OpenCheckpoint | null {
  let best: OpenCheckpoint | null = null;
  for (const field of checkpointFields) {
    const state = checkpointState(field, row, today);
    if (state.status === 'done' || state.status === 'blocked' || state.status === 'waived') {
      continue;
    }
    if (best === null) {
      best = { field, state };
      continue;
    }
    if (state.deadline === null) continue;
    if (best.state.deadline === null || state.deadline < best.state.deadline) {
      best = { field, state };
    }
  }
  return best;
}

// --- calendar arithmetic ----------------------------------------------------

const ISO_DAY = /^\d{4}-\d{2}-\d{2}/;

/**
 * The `YYYY-MM-DD` a value denotes, or null when it denotes no day at all.
 *
 * It accepts a DATETIME's leading date as well as a DATE, because the two temporal
 * families meet here whether this file likes it or not: D1 constrains the base to a DATE
 * field, and a row assembled by a caller that read an instant into the same map would
 * otherwise silently produce no deadline. Taking the leading ten characters is the same
 * decision `record-group.ts` takes when it buckets instants into days.
 */
function dayValue(value: unknown): string | null {
  if (typeof value !== 'string' || !ISO_DAY.test(value)) return null;
  return value.slice(0, 10);
}

/** Calendar arithmetic, not millisecond arithmetic; see the module docblock. */
function addDays(day: string, days: number): string {
  const start = midnight(day);
  return isoDay(new Date(start.getFullYear(), start.getMonth(), start.getDate() + days));
}

/**
 * Whole calendar days from `from` to `to`, negative when `to` is in the past.
 *
 * Both ends are parsed at LOCAL midnight and the difference is divided by a nominal day.
 * The two midnights can be 23 or 25 hours apart across a DST transition, so the result is
 * rounded rather than truncated - which is the whole difference between "due in 1 day" and
 * "due today" on the last Sunday in March.
 */
function daysBetween(from: string, to: string): number {
  return Math.round((midnight(to).getTime() - midnight(from).getTime()) / 86_400_000);
}

function midnight(day: string): Date {
  const [year, month, date] = day.split('-').map(Number);
  if (year === undefined || month === undefined || date === undefined) {
    throw new Error(`not a YYYY-MM-DD day: ${day}`);
  }
  return new Date(year, month - 1, date);
}

function isoDay(date: Date): string {
  const year = String(date.getFullYear()).padStart(4, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
